import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { PromptItem, Category } from '../utils/types'
import ContentViewModal from './ContentViewModal'

interface SortablePromptCardProps {
  prompt: PromptItem
  category?: Category
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onToggleEnabled?: (id: string, enabled: boolean) => void
  onTogglePinned?: (id: string, pinned: boolean) => void
  onCopy: (content: string, id: string) => void
  copiedId: string | null
  isDragOverlay?: boolean
}

const SortablePromptCard: React.FC<SortablePromptCardProps> = ({
  prompt,
  category,
  onEdit,
  onDelete,
  onToggleEnabled,
  onTogglePinned,
  onCopy,
  copiedId,
  isDragOverlay = false,
}) => {
  const [isContentModalOpen, setIsContentModalOpen] = useState(false);
  const [isThumbnailModalOpen, setIsThumbnailModalOpen] = useState(false);
  const [thumbnailLoadFailed, setThumbnailLoadFailed] = useState(false);
  const thumbnailUrl = prompt.thumbnail?.trim();

  useEffect(() => {
    setThumbnailLoadFailed(false);
    setIsThumbnailModalOpen(false);
  }, [prompt.id, thumbnailUrl]);

  useEffect(() => {
    if (!isThumbnailModalOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsThumbnailModalOpen(false);
      }
    };

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isThumbnailModalOpen]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: prompt.id,
    data: {
      type: 'prompt',
      prompt,
    },
    disabled: isDragOverlay,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  }

  // 格式化最后修改时间
  const formatLastModified = (lastModified?: string) => {
    if (!lastModified) return '--'
    
    try {
      const date = new Date(lastModified)
      if (isNaN(date.getTime())) {
        return '--'
      }
      
      const now = new Date()
      const diffInMs = now.getTime() - date.getTime()
      
      if (diffInMs < 0) {
        return date.toLocaleDateString()
      }
      
      const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
      
      if (diffInDays === 0) {
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
        if (diffInHours === 0) {
          const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
          return diffInMinutes <= 1 ? '刚刚' : `${diffInMinutes}分钟前`
        }
        return diffInHours === 1 ? '1小时前' : `${diffInHours}小时前`
      } else if (diffInDays === 1) {
        return '1天前'
      } else if (diffInDays < 7) {
        return `${diffInDays}天前`
      } else {
        return date.toLocaleDateString()
      }
    } catch (err) {
      console.error('格式化时间出错:', err, 'lastModified:', lastModified)
      return '--'
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 flex flex-col ${
        isDragging ? 'shadow-lg scale-105 ring-2 ring-blue-500 ring-opacity-50' : ''
      }`}
    >
      {/* Card Header */}
      <div className={`px-5 py-4 border-b border-gray-100 dark:border-gray-700 ${
        prompt.pinned 
          ? 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20' 
          : 'bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-700'
      }`}>
        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center flex-1 min-w-0'>
            {/* 置顶图标 */}
            {prompt.pinned && (
              <svg className='w-4 h-4 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M5 15l7-7 7 7'/>
              </svg>
            )}
            <h3 className='text-lg font-semibold text-gray-800 dark:text-gray-200 truncate'>{prompt.title}</h3>
          </div>
          
          <div className='flex items-center gap-2 flex-shrink-0'>
            {/* 分类标识 */}
            {category && (
              <div className='flex items-center'>
                <div
                  className='w-3 h-3 rounded-full mr-1.5'
                  style={{ backgroundColor: category.color || '#6366f1' }}
                />
                <span className='text-xs text-gray-600 dark:text-gray-300 font-medium'>{category.name}</span>
              </div>
            )}
            
            {/* 拖拽手柄 */}
            {!isDragOverlay && (
              <div
                {...attributes}
                {...listeners}
                className="w-7 h-7 cursor-grab active:cursor-grabbing flex items-center justify-center rounded-md bg-white dark:bg-gray-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 border border-gray-300 dark:border-gray-600 hover:border-emerald-400 dark:hover:border-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 group"
                title="拖拽重新排序"
              >
                {/* 上下箭头排序图标 - 直观表示排序功能 */}
                <svg className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className='p-5 flex-grow'>
        {/* Tags */}
        <div className='mb-3'>
          {prompt.tags && prompt.tags.length > 0 ? (
            <div className='flex flex-wrap gap-1.5'>
              {prompt.tags.map((tag) => (
                <span
                  key={tag}
                  className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                >
                  #{tag}
                </span>
              ))}
            </div>
          ) : (
            <span className='text-xs text-gray-400 dark:text-gray-500 italic'>无标签</span>
          )}
        </div>

        {/* Thumbnail preview */}
        {thumbnailUrl && !thumbnailLoadFailed && (
          <div className='mb-4 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'>
            <div className='relative aspect-[16/9] bg-gray-100 dark:bg-gray-700'>
              <img
                src={thumbnailUrl}
                alt={`${prompt.title} 缩略图`}
                loading='lazy'
                onError={() => setThumbnailLoadFailed(true)}
                className='h-full w-full object-cover'
              />
              <div className='absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 bg-gradient-to-t from-black/70 to-transparent px-3 pb-3 pt-8'>
                <span className='truncate text-xs font-medium text-white/90'>缩略图</span>
                <button
                  type='button'
                  onClick={() => setIsThumbnailModalOpen(true)}
                  className='inline-flex flex-shrink-0 items-center rounded-lg bg-white/90 px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow-sm transition-all duration-200 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500'
                  title='查看缩略图'
                >
                  <svg className='mr-1.5 h-3.5 w-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                  </svg>
                  查看
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content preview */}
        <div className='mb-4'>
          <div className='bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 transition-all duration-200'>
            <div className='flex items-start justify-between gap-3'>
              <div className='flex-1'>
                <p className='text-sm text-gray-700 dark:text-gray-200 leading-relaxed line-clamp-3 transition-colors duration-200'>
                  {prompt.content}
                </p>
              </div>
              
              {/* 查看内容按钮 */}
              <button
                onClick={() => setIsContentModalOpen(true)}
                className='flex-shrink-0 p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 group'
                title='查看完整内容'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
                </svg>
              </button>
            </div>
            
            {/* 字符统计 */}
            <div className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
              <span>{prompt.content.length} 字符</span>
            </div>
          </div>
        </div>

        {/* 备注 */}
        {prompt.notes && prompt.notes.trim() && (
          <div className='mb-3 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-md'>
            <div className='flex items-start space-x-2'>
              <svg className='w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' />
              </svg>
              <div className='flex-1'>
                <h4 className='text-xs font-medium text-amber-800 dark:text-amber-300 mb-1'>备注</h4>
                <p className='text-xs text-amber-700 dark:text-amber-200 whitespace-pre-wrap'>{prompt.notes}</p>
              </div>
            </div>
          </div>
        )}

        {/* 最后修改时间 */}
        <div className='mb-3 flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400'>
          <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
          </svg>
          <span>最后修改: {formatLastModified(prompt.lastModified)}</span>
        </div>

        {/* 启用状态 */}
        {onToggleEnabled && (
          <div className='mt-2 flex items-center'>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input 
                type='checkbox' 
                checked={prompt.enabled !== undefined ? prompt.enabled : true} 
                onChange={(e) => onToggleEnabled(prompt.id, e.target.checked)}
                className='sr-only peer'
              />
              <div className='relative w-9 h-5 bg-gray-200 dark:bg-gray-600 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[""] after:absolute after:top-1/2 after:right-1/2 after:-translate-y-1/2 after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600'></div>
              <span className='ml-2 text-xs text-gray-600 dark:text-gray-300'>
                {prompt.enabled !== undefined ? (prompt.enabled ? '已启用' : '已停用') : '已启用'}
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Card Footer / Actions */}
      <div className='px-5 py-3 bg-gray-50 dark:bg-gray-700 border-t border-gray-100 dark:border-gray-600 flex justify-between items-center space-x-2'>
        {/* 左侧：数据来源标识 */}
        <div className='flex items-center'>
          {prompt.source === 'remote' && (
            <div className='flex items-center space-x-1.5 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-600/50 rounded-md'>
              <svg className='w-3.5 h-3.5 text-blue-600 dark:text-blue-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z' />
              </svg>
              <span className='text-xs font-medium text-blue-700 dark:text-blue-300'>远程同步</span>
            </div>
          )}
          {prompt.source === 'local' && (
            <div className='flex items-center space-x-1.5 px-2.5 py-1 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-600/50 rounded-md'>
              <svg className='w-3.5 h-3.5 text-green-600 dark:text-green-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              <span className='text-xs font-medium text-green-700 dark:text-green-300'>本地创建</span>
            </div>
          )}
        </div>
        
        {/* 右侧：操作按钮 */}
        <div className='flex items-center space-x-2'>
          {/* 置顶按钮 - 所有数据都可以置顶 */}
          {onTogglePinned && (
            <button
              onClick={() => onTogglePinned(prompt.id, !prompt.pinned)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors duration-200 ${
                prompt.pinned
                  ? 'bg-amber-100 dark:bg-amber-900/50 border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/70'
                  : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-500`}
              title={prompt.pinned ? '取消置顶' : '置顶提示词'}
            >
              <span className='flex items-center'>
                {prompt.pinned ? (
                  <svg className='w-4 h-4 mr-1.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M19 9l-7 7-7-7'/>
                  </svg>
                ) : (
                  <svg className='w-4 h-4 mr-1.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M5 15l7-7 7 7'/>
                  </svg>
                )}
                {prompt.pinned ? '取消置顶' : '置顶'}
              </span>
            </button>
          )}
        
        <button
          onClick={() => onCopy(prompt.content, prompt.id)}
          className={`px-3 py-1.5 text-sm rounded-md border transition-colors duration-200 ${
            copiedId === prompt.id
              ? 'bg-green-100 dark:bg-green-900/50 border-green-300 dark:border-green-600 text-green-700 dark:text-green-400'
              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
          } focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500`}
        >
          <span className='flex items-center'>
            {copiedId === prompt.id ? (
              <>
                <svg
                  className='w-4 h-4 mr-1.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M5 13l4 4L19 7'
                  />
                </svg>
                已复制
              </>
            ) : (
              <>
                <svg
                  className='w-4 h-4 mr-1.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
                  />
                </svg>
                复制
              </>
            )}
          </span>
        </button>

          {/* 编辑按钮 - 仅本地数据可编辑 */}
          {prompt.source !== 'remote' && (
            <button
              onClick={() => onEdit(prompt.id)}
              className='px-3 py-1.5 text-sm rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 transition-colors duration-200'
            >
              <span className='flex items-center'>
                <svg
                  className='w-4 h-4 mr-1.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
                  />
                </svg>
                编辑
              </span>
            </button>
          )}

          {/* 删除按钮 - 仅本地数据可删除 */}
          {prompt.source !== 'remote' && (
            <button
              onClick={() => onDelete(prompt.id)}
              className='px-3 py-1.5 text-sm rounded-md bg-white dark:bg-gray-800 border border-red-300 dark:border-red-600 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-500 transition-colors duration-200'
            >
              <span className='flex items-center'>
                <svg
                  className='w-4 h-4 mr-1.5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth='2'
                    d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16'
                  />
                </svg>
                删除
              </span>
            </button>
          )}
        </div>
      </div>
      
      {/* 内容查看弹窗 */}
      <ContentViewModal
        isOpen={isContentModalOpen}
        onClose={() => setIsContentModalOpen(false)}
        title={prompt.title}
        content={prompt.content}
      />

      {/* 缩略图查看弹窗 */}
      {thumbnailUrl && isThumbnailModalOpen && createPortal(
        <div className='fixed inset-0 z-50 overflow-y-auto'>
          <div
            className='fixed inset-0 bg-black/70 backdrop-blur-sm'
            onClick={() => setIsThumbnailModalOpen(false)}
            aria-hidden='true'
          />
          <div className='flex min-h-screen items-center justify-center p-4'>
            <div
              className='relative w-full max-w-4xl overflow-hidden rounded-2xl bg-white dark:bg-gray-900 shadow-2xl'
              onClick={(e) => e.stopPropagation()}
            >
              <div className='flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-5 py-4'>
                <div className='min-w-0'>
                  <h3 className='truncate text-lg font-semibold text-gray-800 dark:text-gray-100'>{prompt.title}</h3>
                  <p className='mt-1 truncate text-xs text-gray-500 dark:text-gray-400'>{thumbnailUrl}</p>
                </div>
                <button
                  type='button'
                  onClick={() => setIsThumbnailModalOpen(false)}
                  className='ml-4 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200'
                  aria-label='关闭缩略图'
                >
                  <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                  </svg>
                </button>
              </div>
              <div className='bg-gray-950 p-4'>
                <img
                  src={thumbnailUrl}
                  alt={`${prompt.title} 缩略图大图`}
                  className='mx-auto max-h-[72vh] w-auto max-w-full rounded-lg object-contain'
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default SortablePromptCard
